import React from "react";
import { Box, IconButton, TableCell, TableRow } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DotLegalLink, DotLegalSelect, DotLegalTable, ITableHeader } from "@dotlegal/dotlegal-ui-components";
import { EntityType } from "@common/Common.types.ts";
import { PurpleLockIcon } from "@common/icons.tsx";
import EntityTypeIconWithName from "@common/components/entityTypeIcon/EntityTypeIconWithName.tsx";
import DotLegalTableWithControlsLocalized from "@common/components/dotLegalTableWithControlsLocalized/DotLegalTableWithControlsLocalized.tsx";
import { isNullOrWhitespace } from "@common/stringOperations.ts";
import { useTranslation } from "@localization/useTranslation.ts";
import { useUrlProvider } from "../../../useUrlProvider.ts";
import SearchResultListContent from "@common/components/search/SearchResultListContent.tsx";
import { getLink } from "../../../taskManagement/taskPane/taskRelationsBox/TaskRelationsBox.hooks.ts";
import { EntityRelationsTableRow, useEntityRelationsTable } from "./EntityRelationsTable.hooks.ts";

const relationsPerPage = 30;

export interface EntityRelationsTableProps {
    entityId: string;
    entityType: EntityType;
    canEdit: boolean;
}

function EntityRelationsTable(props: EntityRelationsTableProps) {
    const { translateString } = useTranslation();
    const urlProvider = useUrlProvider();
    const {
        isLoading,
        rows,
        hasRelations,
        canAccessRelations,
        removeRelation,
        relationSuggestions,
        isLoadingRelationSuggestions,
        isEntitySpecificSearch,
        onRelationSearchFocus,
        onRelationSearchChange,
        onShowMoreRelationsClick,
        onRelationSelected,
    } = useEntityRelationsTable(props.entityId, props.entityType);

    const headers: Array<ITableHeader<EntityRelationsTableRow>> = [
        { property: "entityName", text: translateString("name"), align: "left", showOnMobile: true },
        { property: "entityTypeName", text: translateString("type"), align: "left", showOnMobile: true },
    ];

    const tableProps: React.ComponentProps<typeof DotLegalTable<EntityRelationsTableRow>> = {
        headers: headers,
        data: rows,
        defaultOrderBy: "entityName",
        defaultOrder: "asc",
        isLoading: isLoading,
        clickableRows: false,
        hasOverflowMenu: props.canEdit,
        emptyText: translateString("noRelationsOnEntity"),
        renderRow: (row) => (
            <TableRow key={row.relationId}>
                <TableCell>
                    <DotLegalLink
                        bold
                        linkColor={"primary"}
                        to={getLink(row.entityType, row.entityId, row.parentId, urlProvider, row.subjectEntityType, row.subjectEntityId)}
                    >
                        {row.entityName}
                    </DotLegalLink>
                </TableCell>
                <TableCell>
                    <EntityTypeIconWithName type={row.entityType} name={row.entityTypeName} />
                </TableCell>
                {props.canEdit && (
                    <TableCell align={"right"}>
                        <IconButton size="small" onClick={() => removeRelation(row.relationId)}>
                            <CloseIcon sx={(theme) => ({ fill: theme.palette.error.main })} />
                        </IconButton>
                    </TableCell>
                )}
            </TableRow>
        ),
    };

    return (
        <Box sx={{ mb: 3 }}>
            {canAccessRelations ? (
                <>
                    {props.canEdit && (
                        // The relation step spans the page, and a search field that wide stretches its suggestion list with it.
                        <Box sx={{ mb: 1, mx: "auto", width: { xs: "100%", md: "50%" } }}>
                            <DotLegalSelect
                                label={translateString("addRelation")}
                                // The select has no options of its own: the search results, grouped by type, take the place of its option list.
                                options={[]}
                                onChange={() => undefined}
                                paperSx={{ "& .MuiAutocomplete-noOptions": { display: "none" } }}
                                onFocus={onRelationSearchFocus}
                                onSearchValueChange={onRelationSearchChange}
                                disabled={isNullOrWhitespace(props.entityId)}
                                noOptionsLabel={translateString("noOptions")}
                            >
                                {/* Keeps the focus in the field while a result is clicked, so the list stays open until the selection is made.
                                    The padding and font size mirror the paper SearchResultList normally wraps its content in. */}
                                <Box onMouseDown={(e) => e.preventDefault()} sx={(theme) => ({ padding: 2, fontSize: theme.typography.pxToRem(14) })}>
                                    <SearchResultListContent
                                        items={relationSuggestions}
                                        isLoading={isLoadingRelationSuggestions}
                                        isEntitySpecificSearch={isEntitySpecificSearch}
                                        onItemSelected={onRelationSelected}
                                        onShowMoreClick={onShowMoreRelationsClick}
                                    />
                                </Box>
                            </DotLegalSelect>
                        </Box>
                    )}
                    {hasRelations &&
                        (props.canEdit ? (
                            <DotLegalTableWithControlsLocalized {...tableProps} getUserSpecificPageLength={() => relationsPerPage} />
                        ) : (
                            // Read-only relations are shown on the overview, where a search field would crowd the step.
                            <DotLegalTable {...tableProps} rowsPerPage={relationsPerPage} paginationLabelOf={translateString("labelOf")} />
                        ))}
                </>
            ) : (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {PurpleLockIcon}
                    {translateString("upgradePrivacyPlanToday")}
                </Box>
            )}
        </Box>
    );
}

export default EntityRelationsTable;
